import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { testimonials } from '@/mocks/testimonials';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('seo.aboutTitle');
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', t('seo.aboutDesc'));
  }, [t]);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'mm131写真图库',
    url: 'https://www.mm131.com',
    description: '国内领先的高清美女写真图库平台',
    foundingDate: '2015',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-1314',
      contactType: 'customer service',
      email: 'contact@mm131.com',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '张江高科技园区博云路2号 mm131大厦',
      addressLocality: '上海',
      addressRegion: '浦东新区',
      addressCountry: 'CN',
    },
  };

  return (
    <div className="pt-16">
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <Breadcrumb items={[{ label: t('breadcrumb.about') }]} />

      <section className="w-full py-8 md:py-12 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-14">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {t('about.title')}
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600">
              {t('about.subtitle')}
            </p>
          </div>

          <section className="mb-12 md:mb-16">
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-5 flex items-center gap-2">
              <span className="w-1 h-5 bg-primary-500 rounded-full inline-block"></span>
              {t('about.brandIntro')}
            </h2>
            <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>{t('about.brandDesc1')}</p>
              <p>{t('about.brandDesc2')}</p>
              <p>{t('about.brandDesc3')}</p>
            </div>
          </section>

          <section className="mb-12 md:mb-16">
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-5 flex items-center gap-2">
              <span className="w-1 h-5 bg-accent-500 rounded-full inline-block"></span>
              {t('about.reputation')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((item) => (
                <div
                  key={item.id}
                  className="bg-background-100 rounded-xl p-5 md:p-6 flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed flex-1">
                    "{item.content}"
                  </p>
                  <div className="flex gap-0.5 mt-3">
                    {Array.from({ length: item.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-5 flex items-center gap-2">
              <span className="w-1 h-5 bg-secondary-500 rounded-full inline-block"></span>
              {t('about.contactTitle')}
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              {t('about.contactDesc')}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {[
                { icon: 'ri-mail-line', label: t('about.email'), value: t('about.emailValue'), color: 'primary' },
                { icon: 'ri-phone-line', label: t('about.phone'), value: t('about.phoneValue'), color: 'accent' },
                { icon: 'ri-map-pin-line', label: t('about.address'), value: t('about.addressValue'), color: 'secondary' },
                { icon: 'ri-wechat-line', label: t('about.wechat'), value: t('about.wechatValue'), color: 'primary' },
                { icon: 'ri-weibo-line', label: t('about.weibo'), value: t('about.weiboValue'), color: 'accent' },
                { icon: 'ri-time-line', label: t('about.businessHours'), value: t('about.businessHoursValue'), color: 'secondary' },
              ].map((item, index) => {
                const colorMap: Record<string, string> = {
                  primary: 'bg-primary-100/70 text-primary-600',
                  accent: 'bg-accent-100/80 text-accent-700',
                  secondary: 'bg-secondary-100/80 text-secondary-700',
                };
                return (
                  <div key={index} className="bg-background-100 rounded-xl p-5 flex items-start gap-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${colorMap[item.color]}`}>
                      <i className={item.icon}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-500 mb-0.5">{item.label}</p>
                      <p className="text-sm font-medium text-foreground-900 break-all">{item.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </section>
    </div>
  );
}